import { useFormContext } from "react-hook-form";
import { ChangeEvent, useCallback, useMemo } from "react";
import debounce from "lodash-es/debounce";

import FormField from "ds/components/Form/Field";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import Input from "ds/components/Input";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { validateUrlField } from "utils/formValidators";

import { extractRepositoryDetails } from "./utils";
import RepositoryTooltip from "../RepositoryTooltip";
import { getTooltipAnalyticsProps } from "../utils";
import { SourceCodeProjects, SourceCodeSettingsField, SourceCodeFields } from "../types";
import { SOURCE_CODE_FIELDS_TOOLTIPS } from "../constants";

const DEBOUNCE_DELAY = 300;

type SourceCodeRepositoryUrlFieldProps = {
  analyticsVersion?: string;
  projectType: SourceCodeProjects;
};

const SourceCodeRepositoryUrlField = ({
  analyticsVersion,
  projectType,
}: SourceCodeRepositoryUrlFieldProps) => {
  const { register, watch, trigger, setValue, setError, formState } =
    useFormContext<SourceCodeSettingsField>();

  const formValues = watch();

  const handleRepositoryURLChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const { namespace, repository, err } = extractRepositoryDetails(value);
      setValue("repositoryURL", value, { shouldDirty: true });
      setValue("namespace", namespace || "", { shouldDirty: true });
      setValue("repository", repository || "", { shouldDirty: true });
      setValue("branch", "", { shouldDirty: true });

      if (value && err) {
        setError("repositoryURL", {
          type: "custom",
          message: err,
        });
        return;
      }

      trigger("repositoryURL");
    },
    [setError, setValue, trigger]
  );

  const handleRepositoryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValue("repository", value || "", { shouldDirty: true });
      setValue("branch", "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleNamespaceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setValue("namespace", value || "", { shouldDirty: true });
      setValue("branch", "", { shouldDirty: true });
    },
    [setValue]
  );

  const debouncedUrlChange = useMemo(
    () => debounce(handleRepositoryURLChange, DEBOUNCE_DELAY),
    [handleRepositoryURLChange]
  );

  const debouncedRepositoryChange = useMemo(
    () => debounce(handleRepositoryChange, DEBOUNCE_DELAY),
    [handleRepositoryChange]
  );

  const debouncedNamespaceChange = useMemo(
    () => debounce(handleNamespaceChange, DEBOUNCE_DELAY),
    [handleNamespaceChange]
  );

  return (
    <>
      <FormField
        label="URL"
        error={formState.errors?.repositoryURL?.message}
        {...getTooltipAnalyticsProps("Source Code", "URL", projectType, {
          provider: formValues.provider,
          version: analyticsVersion,
        })}
        tooltipInfo={
          <>
            <TooltipModalTitle>Repository URL</TooltipModalTitle>
            <TooltipModalBody align="start">
              {SOURCE_CODE_FIELDS_TOOLTIPS[SourceCodeFields.RepositoryURL][projectType].text}
              <ReadMoreDocsLink
                docsUrl={
                  SOURCE_CODE_FIELDS_TOOLTIPS[SourceCodeFields.RepositoryURL][projectType].link
                }
              />
            </TooltipModalBody>
          </>
        }
        tooltipInfoVariant="modal"
        noMargin
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="Public repository URL"
            error={!!formState.errors?.repositoryURL}
            {...register("repositoryURL", {
              validate: validateUrlField(),
            })}
            onChange={debouncedUrlChange}
            {...ariaInputProps}
          />
        )}
      </FormField>
      {formValues.repositoryURL && formValues.provider && (
        <>
          <FormField
            label="Repository"
            {...getTooltipAnalyticsProps("Source Code", "Repository", projectType, {
              provider: formValues.provider,
              version: analyticsVersion,
            })}
            tooltipInfo={
              <RepositoryTooltip vcsProvider={formValues.provider} projectType={projectType} />
            }
            tooltipInfoVariant="modal"
            noMargin
          >
            {({ ariaInputProps }) => (
              <Input
                placeholder="Repository name"
                {...register("repository", { onChange: debouncedRepositoryChange })}
                {...ariaInputProps}
              />
            )}
          </FormField>
          <FormField
            noMargin
            label="Namespace"
            tooltipInfoVariant="modal"
            tooltipInfo={
              <>
                <TooltipModalTitle>Namespace</TooltipModalTitle>
                <TooltipModalBody align="start">
                  {HUMANIZE_PROVIDER[formValues.provider]} repository namespace.
                </TooltipModalBody>
              </>
            }
          >
            {({ ariaInputProps }) => (
              <Input
                placeholder="Repository namespace"
                {...register("namespace", { onChange: debouncedNamespaceChange })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </>
      )}
    </>
  );
};

export default SourceCodeRepositoryUrlField;
